import React, { useState } from 'react'
import DatePicker, { registerLocale, setDefaultLocale } from 'react-datepicker'
import { Controller } from 'react-hook-form'
import 'react-datepicker/dist/react-datepicker.css'
import { fr } from 'date-fns/locale'
import { useIntl } from 'react-intl'
import { getDay, setHours, setMinutes } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import DynamicFormattedMessage from '../common/ui/DynamicFormattedMessage'
import { IDateGroupParams } from '../../interfaces/IForms'
import ErrorMessage from './ErrorMessage'
import { IAppointmentDate } from '../../interfaces/IAppointments'
import { DATEPICKER_FORMAT } from '../../enums/common'
import { useInitiateAppointment } from '../../hooks/clientDetails/UseInitiateAppointment'
import { advancedFiltersInitialState } from '../../constants/filters'

registerLocale('fr', fr)
setDefaultLocale('fr')

const FormGroupDate = ({
  startDate,
  endDate,
  customClass,
  rawChange = false,
  startDateOnly,
  allowWeekend,
  isClearable = false,
  setPortalId = false,
  checkBookedDate,
  isUnavailable,
  resetIsUnavailable,
}: IDateGroupParams) => {
  const intl = useIntl()
  const { setValue } = startDate
  const filterPassedTime = (time: number) =>
    new Date().getTime() < new Date(time).getTime()
  const timeCaption = intl.formatMessage({
    id: `datepicker.timeCaption.label`,
  })

  const isWeekday = (date: Date) => getDay(date) !== 0 && getDay(date) !== 6
  const [alreadyBooked, setAlreadyBooked] = useState(false)
  const { allAppointments } = useInitiateAppointment(
    advancedFiltersInitialState
  )

  const handleResetIsUnavailable = () => {
    if (resetIsUnavailable) {
      resetIsUnavailable()
    }
  }

  return (
    <div className="datePickerGroupWrapper alignItemsEnd dFlex row">
      <div
        className={`${
          !startDateOnly ? 'col6' : 'col12'
        } dFlex datePickerElement flexColumn ${
          isClearable ? 'isClearable' : ''
        } ${!startDate.value ? 'withoutValue' : ''} ${customClass || ''}`}
      >
        {startDate.label && (
          <DynamicFormattedMessage
            id={startDate.label}
            className="inputLabel wFull"
          />
        )}
        <Controller
          name={startDate.id}
          control={startDate.control}
          defaultValue={startDate.value}
          rules={startDate.rules}
          render={({ onChange }) => (
            <DatePicker
              portalId={setPortalId ? 'root-portal' : ''}
              locale="fr"
              selected={startDate.value}
              value={startDate.value}
              className={startDate.classes}
              selectsStart
              startDate={startDate.value}
              endDate={endDate?.value || null}
              maxDate={startDate.maxDate || endDate?.value || null}
              filterTime={startDate.filterTime ? filterPassedTime : null}
              minDate={startDate.minDate || null}
              dateFormat={startDate.format || DATEPICKER_FORMAT}
              data-qa={startDate.id}
              timeCaption={timeCaption}
              showTimeSelect={startDate.displayTime || false}
              onChange={(value: Date) => {
                onChange(value)
                setValue((prevState: IAppointmentDate) => {
                  return {
                    ...prevState,
                    startDate: value,
                  }
                })
                if (checkBookedDate) {
                  const initialRdvStartDate = value
                  const rdvStartDate = new Date(initialRdvStartDate)
                  const rdvEndDate = new Date(value)

                  rdvEndDate.setTime(rdvStartDate.getTime() + 60 * 60 * 1000)

                  const isOverlap = allAppointments.some((appointment) => {
                    if (appointment.start && appointment.end) {
                      const appointmentStart = utcToZonedTime(
                        appointment.start,
                        'UTC'
                      )
                      const appointmentEnd = utcToZonedTime(
                        appointment.end,
                        'UTC'
                      )

                      return (
                        rdvStartDate < appointmentEnd &&
                        rdvEndDate > appointmentStart
                      )
                    }
                    return false
                  })

                  if (isOverlap) {
                    setAlreadyBooked(true)
                  } else {
                    setAlreadyBooked(false)
                    handleResetIsUnavailable()
                  }
                }
              }}
              filterDate={allowWeekend ? () => allowWeekend : isWeekday}
              minTime={setHours(0, 6)}
              maxTime={setHours(setMinutes(new Date(), 30), 20)}
              onChangeRaw={(e: Event) =>
                rawChange ? () => {} : e.preventDefault()
              }
              isClearable={isClearable}
            />
          )}
        />
        <ErrorMessage error={startDate.error} />
        {(alreadyBooked || isUnavailable) && (
          <DynamicFormattedMessage
            tag="span"
            id="calendar.appointment.error.message"
            className="errorMessage"
          />
        )}
      </div>
      {!startDateOnly && (
        <div
          className={`col6 dFlex datePickerElement flexColumn  ${
            isClearable ? 'isClearable' : ''
          } ${!endDate.value ? 'withoutValue' : ''} ${customClass || ''}`}
        >
          {endDate.label && (
            <DynamicFormattedMessage
              id={endDate.label}
              className="inputLabel wFull"
            />
          )}
          <Controller
            name={endDate.id}
            control={endDate.control}
            defaultValue={endDate.value}
            rules={endDate.rules}
            render={({ onChange }) => (
              <DatePicker
                locale="fr"
                selected={endDate.value}
                value={endDate.value}
                className={endDate.classes}
                selectsEnd
                startDate={startDate.value}
                endDate={endDate.value}
                minDate={endDate.minDate || startDate.value}
                dateFormat={startDate.format || DATEPICKER_FORMAT}
                data-qa={endDate.id}
                timeCaption={timeCaption}
                showTimeSelect={endDate.displayTime || false}
                filterTime={endDate.filterTime ? filterPassedTime : null}
                onChange={(value: Date) => {
                  onChange(value)
                  setValue((prevState: IAppointmentDate) => {
                    return {
                      ...prevState,
                      endDate: value,
                    }
                  })
                }}
                onChangeRaw={(e: Event) => e.preventDefault()}
                isClearable={isClearable}
              />
            )}
          />
          <ErrorMessage error={endDate.error} />
        </div>
      )}
    </div>
  )
}

export default FormGroupDate
